/**
 * Phase 2G · Temporary public cover page (Concrete Control LLC referral).
 *
 * To restore the product-landing at `/`, revert the `/` route in App.js
 * back to <Landing /> (currently mapped to <PublicHome />) and delete this
 * file. All product code intact — no product component was removed.
 *
 * The global <meta name="robots"> tag in /app/frontend/public/index.html
 * is set to `noindex, nofollow`. This component swaps it to `index, follow`
 * on mount and restores on unmount so /only this route/ is indexable.
 */
import React, { useEffect } from "react";

const ROBOTS_INDEX = "index, follow";
const ROBOTS_NOINDEX = "noindex, nofollow";
const PAGE_TITLE = "Concrete Control LLC";
const PAGE_DESC = "Concrete Control LLC is a New Mexico company. Public work is at Durable Designs and MarkovLab.";

function setMeta(name, content) {
    let el = document.querySelector(`meta[name="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
    return el;
}

export default function PublicHome() {
    useEffect(() => {
        const priorTitle = document.title;
        const robotsEl = document.querySelector('meta[name="robots"]');
        const priorRobots = robotsEl ? robotsEl.getAttribute("content") : ROBOTS_NOINDEX;
        const descEl = document.querySelector('meta[name="description"]');
        const priorDesc = descEl ? descEl.getAttribute("content") : "";

        document.title = PAGE_TITLE;
        setMeta("robots", ROBOTS_INDEX);
        setMeta("description", PAGE_DESC);

        return () => {
            document.title = priorTitle;
            setMeta("robots", priorRobots || ROBOTS_NOINDEX);
            if (priorDesc) setMeta("description", priorDesc);
        };
    }, []);

    return (
        <main data-testid="public-home" className="min-h-screen bg-[#fafafa] text-[#0e1416] flex flex-col">
            <div className="flex-1 mx-auto w-full max-w-[960px] px-6 sm:px-10 py-16 sm:py-24">
                <header>
                    <h1 data-testid="public-home-title" className="text-[32px] sm:text-[44px] leading-[1.1] font-medium tracking-[-0.015em]">
                        Concrete Control LLC
                    </h1>
                    <p className="mt-3 text-[15px] text-[#5a6669]">
                        A New Mexico limited liability company.
                    </p>
                </header>

                <section className="mt-14 sm:mt-20">
                    <p className="text-[14px] tracking-[0.06em] uppercase text-[#5a6669] mb-6">
                        Our public work is here:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <a
                            data-testid="public-home-card-durable"
                            href="https://www.durabledesigns.co.uk/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group border border-[#d8dcdd] bg-[#ffffff] p-7 sm:p-8 hover:border-[#0e1416] transition-colors flex flex-col"
                        >
                            <h2 className="text-[20px] font-medium">Durable Designs</h2>
                            <p className="mt-3 text-[14px] text-[#5a6669] leading-[1.65] flex-1">
                                Specialist consultancy in materials, durability, and intelligent digital tools for infrastructure.
                            </p>
                            <span className="mt-6 tc-mono text-[11.5px] tracking-[0.06em] text-[#0e1416] group-hover:underline">
                                Visit durabledesigns.co.uk →
                            </span>
                        </a>

                        <a
                            data-testid="public-home-card-markovlab"
                            href="https://markovlab.app/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group border border-[#d8dcdd] bg-[#ffffff] p-7 sm:p-8 hover:border-[#0e1416] transition-colors flex flex-col"
                        >
                            <h2 className="text-[20px] font-medium">MarkovLab</h2>
                            <p className="mt-3 text-[14px] text-[#5a6669] leading-[1.65] flex-1">
                                Probabilistic options intelligence for self-directed traders.
                            </p>
                            <span className="mt-6 tc-mono text-[11.5px] tracking-[0.06em] text-[#0e1416] group-hover:underline">
                                Visit markovlab.app →
                            </span>
                        </a>
                    </div>
                </section>

                <section data-testid="public-home-registered-office" className="mt-16 sm:mt-24 border-t border-[#d8dcdd] pt-8">
                    <p className="text-[11.5px] tracking-[0.14em] uppercase text-[#5a6669] mb-3">
                        Registered office
                    </p>
                    <address className="not-italic tc-mono text-[13px] leading-[1.8] text-[#0e1416]">
                        Concrete Control LLC<br />
                        1209 Mountain Road Pl NE, Ste N<br />
                        Albuquerque, NM 87110<br />
                        United States
                    </address>
                </section>
            </div>

            <footer data-testid="public-home-footer" className="border-t border-[#d8dcdd] bg-[#ffffff]">
                <div className="mx-auto max-w-[960px] px-6 sm:px-10 py-8 tc-mono text-[11px] text-[#5a6669] leading-[1.7]">
                    <p>
                        Durable Designs is a trading name of Concrete Control LLC. MarkovLab operates under Concrete Control LLC.
                    </p>
                    <p className="mt-1">© 2026 Concrete Control LLC</p>
                </div>
            </footer>
        </main>
    );
}
